import { Plus, Trash2 } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useOperationForm } from "../hooks/use-operation-form";
import { useTemplates } from "../hooks/use-template-queries";
import { AVAILABLE_SCREENS } from "../constants/screens";
import { Operation, OpsTemplate } from "../types";

interface OperationFormDialogProps {
  operation: Operation | null;
  isOpen: boolean;
  onClose: () => void;
}

const AVAILABLE_DATABASES = [
  { value: "xama", label: "XAMA" },
  { value: "xpto", label: "XPTO" },
  { value: "rondolog", label: "Rondolog" },
  { value: "gestor", label: "Gestor" },
  { value: "muve", label: "Muve" },
];

export function OperationFormDialog({
  operation,
  isOpen,
  onClose,
}: OperationFormDialogProps) {
  const {
    formData,
    setFormData,
    uploads,
    errors,
    uploadErrors,
    apiError,
    isLoading,
    addUpload,
    removeUpload,
    updateUpload,
    handleSubmit,
    applyTemplate,
  } = useOperationForm({ operation, isOpen, onClose });

  const { data: templates = [] } = useTemplates();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {operation ? "Editar Operação" : "Adicionar Operação"}
          </DialogTitle>
          <DialogDescription>
            {operation
              ? "Atualize as informações da operação"
              : "Adicione uma nova operação ao sistema"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {apiError && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-800">{apiError}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="ops">
              Operação (identificador) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="ops"
              value={formData.ops}
              onChange={(e) =>
                setFormData({ ...formData, ops: e.target.value.toLowerCase() })
              }
              disabled={!!operation}
              placeholder="ex: rayo, muve, bee"
            />
            {errors.ops && <p className="text-sm text-red-600">{errors.ops}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="db_name">
              Banco de Dados <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.db_name}
              onValueChange={(value) =>
                setFormData({ ...formData, db_name: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o banco de dados" />
              </SelectTrigger>
              <SelectContent>
                {AVAILABLE_DATABASES.map((db) => (
                  <SelectItem key={db.value} value={db.value}>
                    {db.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.db_name && (
              <p className="text-sm text-red-600">{errors.db_name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Telas</Label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_SCREENS.map((screen) => {
                const isSelected = formData.screens.includes(screen.value);
                return (
                  <Badge
                    key={screen.value}
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-label={screen.label}
                    tabIndex={0}
                    className="cursor-pointer select-none transition-all hover:opacity-80 text-sm px-3 py-1.5"
                    style={
                      isSelected
                        ? {
                            backgroundColor: "hsl(221, 83%, 53%)",
                            borderColor: "hsl(221, 83%, 53%)",
                            color: "#fff",
                          }
                        : {
                            backgroundColor: "transparent",
                            borderColor: "#94a3b8",
                            color: "#64748b",
                          }
                    }
                    onClick={() =>
                      setFormData({
                        ...formData,
                        screens: isSelected
                          ? formData.screens.filter((s) => s !== screen.value)
                          : [...formData.screens, screen.value],
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        setFormData({
                          ...formData,
                          screens: isSelected
                            ? formData.screens.filter((s) => s !== screen.value)
                            : [...formData.screens, screen.value],
                        });
                      }
                    }}
                  >
                    {screen.label}
                  </Badge>
                );
              })}
            </div>
          </div>

          {templates.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="template-select">Aplicar Template (opcional)</Label>
              <Select
                onValueChange={(value) => {
                  const tpl = templates.find((t: OpsTemplate) => String(t.id) === value);
                  if (tpl) applyTemplate(tpl);
                }}
              >
                <SelectTrigger id="template-select">
                  <SelectValue placeholder="Selecione um template para pré-preencher" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((tpl: OpsTemplate) => (
                    <SelectItem key={tpl.id} value={String(tpl.id)}>
                      {tpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Configurações de Upload</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addUpload}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Upload
              </Button>
            </div>

            {uploads.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Nenhuma configuração de upload adicionada
              </p>
            )}

            {uploads.map((upload, index) => (
              <div
                key={index}
                className="border rounded-lg p-4 space-y-3 relative"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-sm">Upload #{index + 1}</h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeUpload(index)}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>

                {uploadErrors[index] && (
                  <p className="text-sm text-red-600 mb-2">
                    {uploadErrors[index]}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor={`upload-key-${index}`} className="text-xs">
                      Key <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`upload-key-${index}`}
                      value={upload.key}
                      onChange={(e) =>
                        updateUpload(index, "key", e.target.value)
                      }
                      placeholder="ex: romaneio"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor={`upload-title-${index}`}
                      className="text-xs"
                    >
                      Titulo <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`upload-title-${index}`}
                      value={upload.title}
                      onChange={(e) =>
                        updateUpload(index, "title", e.target.value)
                      }
                      placeholder="ex: Upload Romaneio"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor={`upload-doc-type-${index}`}
                      className="text-xs"
                    >
                      Tipo de Documento <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`upload-doc-type-${index}`}
                      value={upload.doc_type}
                      onChange={(e) =>
                        updateUpload(index, "doc_type", e.target.value)
                      }
                      placeholder="ex: romaneio"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor={`upload-extension-${index}`}
                      className="text-xs"
                    >
                      Extensão do Arquivo{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`upload-extension-${index}`}
                      value={upload.extension_file}
                      onChange={(e) =>
                        updateUpload(index, "extension_file", e.target.value)
                      }
                      placeholder="ex: .xlsx"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor={`upload-upload-title-${index}`}
                      className="text-xs"
                    >
                      Titulo do Upload <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`upload-upload-title-${index}`}
                      value={upload.upload_title}
                      onChange={(e) =>
                        updateUpload(index, "upload_title", e.target.value)
                      }
                      placeholder="ex: Planilha de Romaneio"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Salvando..." : operation ? "Atualizar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
